import { InsuranceChatHook } from './interfaces/ai';
import { getActiveSkin } from '@utils/index';

export interface WebSocketMessage {
  message: string;
  context?: {
    page?: string;
    applicationDTO?: string;
    page_context?: string;
    activeCategories?: string[];
    name?: {
      first_name: string;
      last_name: string;
    };
    isFirstVisit?: boolean;
    isPHQEnabled?: boolean;
    signedAt?: string | null;
    hasCompletedApplication?: boolean;
    progress?: number;
    abandonedAt?: string | null;
    updatedAt?: string | null;
  };
  can_hooks?: boolean;
}

export interface ChunkMessage {
  type: 'chunk';
  content: string;
}

export interface EndMessage {
  type: 'end';
  hooks?: InsuranceChatHook[];
}

export interface ErrorMessage {
  type: 'error';
  error: string;
}

export interface PongMessage {
  type: 'pong';
}

export type ServerMessage = ChunkMessage | EndMessage | ErrorMessage | PongMessage;

type ChunkCallback = (content: string) => void;
type EndCallback = (hooks: InsuranceChatHook[]) => void;
type ErrorCallback = (error: string) => void;
type OpenCallback = () => void;
type CloseCallback = (wasStreaming: boolean) => void;
type ReconnectCallback = () => void;

class ChatWebSocketService {
  private ws: WebSocket | null = null;
  private chunkCallbacks: ChunkCallback[] = [];
  private endCallbacks: EndCallback[] = [];
  private errorCallbacks: ErrorCallback[] = [];
  private openCallbacks: OpenCallback[] = [];
  private closeCallbacks: CloseCallback[] = [];
  private reconnectCallbacks: ReconnectCallback[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private lastPongTime: number = 0;
  private isStreaming = false;
  private hasPendingMessage = false;
  private responseTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly RESPONSE_TIMEOUT_MS = 30000; // 30s — if no response, fire error
  private connectionParams: {
    userId?: string;
    groupHash?: string;
    sessionId?: string;
    isPHQEnabled?: boolean;
  } = {};

  constructor() {
    // Listen for network changes (online/offline)
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  private handleOnline = () => {
    console.log('[WebSocket] Network online, attempting reconnect...');
    if (!this.isConnected() && this.connectionParams.userId) {
      this.reconnectAttempts = 0; // Reset attempts on network restore
      this.scheduleReconnect();
    }
  };

  private handleOffline = () => {
    console.log('[WebSocket] Network offline');
    // Connection will be closed by browser, onclose will handle cleanup
  };

  private getBaseUrl(): string {
    const baseUrl = import.meta.env.VITE_AI_API_BASE_URL || 'wss://ai.dev.virgilcare.com';
    const fixedBaseUrl = baseUrl.replace('https://', 'wss://');
    return `${fixedBaseUrl}ws/chat`;
  }

  private buildUrl(): string {
    const baseUrl = this.getBaseUrl();
    const params = new URLSearchParams();

    const apiKey = import.meta.env.VITE_AI_KEY;
    if (apiKey) {
      params.append('token', apiKey);
    }
    if (this.connectionParams.userId) {
      params.append('user_id', this.connectionParams.userId);
    }
    if (this.connectionParams.groupHash) {
      params.append('group_hash', this.connectionParams.groupHash);
    }
    if (this.connectionParams.sessionId) {
      params.append('chat_session_id', this.connectionParams.sessionId);
    }
    if (this.connectionParams.isPHQEnabled !== undefined) {
      params.append('isPHQEnabled', String(this.connectionParams.isPHQEnabled));
    }
    const groupType =
      document.querySelector('meta[name="x-group-type"]')?.getAttribute('content') ||
      (getActiveSkin() === 'lolly' ? 'lolly' : 'default');
    
    console.log('[AI API] x-group-type:', groupType);

    params.append('group_type', String(groupType));  

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  setStreaming(streaming: boolean): void {
    this.isStreaming = streaming;
  }

  connect(
    userId?: string,
    groupHash?: string,
    sessionId?: string,
    isPHQEnabled?: boolean
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Store connection params for potential reconnection
      this.connectionParams = { userId, groupHash, sessionId, isPHQEnabled };

      // Close existing connection if any
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }

      // Clear ping interval
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }

      let settled = false;

      // Quick offline check
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        reject(new Error('No internet connection'));
        return;
      }

      const url = this.buildUrl();
      console.log('[WebSocket] Connecting to:', url);

      // Timeout if connection doesn't open within 10s
      const connectTimeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          console.warn('[WebSocket] Connection timeout');
          if (this.ws) {
            this.ws.close();
            this.ws = null;
          }
          reject(new Error('Connection timeout'));
        }
      }, 10000);

      try {
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          clearTimeout(connectTimeout);
          console.log('[WebSocket] Connected');
          this.reconnectAttempts = 0;
          this.lastPongTime = Date.now();
          // connection established
          this.startPing();
          this.openCallbacks.forEach((cb) => cb());
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            // Update last pong time on any message (server is alive)
            this.lastPongTime = Date.now();
            
            const data: ServerMessage = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (e) {
            console.error('[WebSocket] Failed to parse message:', e);
          }
        };

        this.ws.onerror = (error) => {
          console.error('[WebSocket] Error:', error);
          clearTimeout(connectTimeout);
          if (!settled) {
            settled = true;
            reject(new Error('WebSocket connection error'));
          }
        };

        this.ws.onclose = (event) => {
          console.log('[WebSocket] Closed:', event.code, event.reason);

          // Clear ping interval
          if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
          }

          const wasActive = this.isStreaming || this.hasPendingMessage;
          this.isStreaming = false;

          // Notify close so store updates wsConnected
          this.closeCallbacks.forEach((cb) => cb(wasActive));

          // Reject connect promise if still pending
          if (!settled) {
            settled = true;
            reject(new Error('WebSocket connection closed'));
            return;
          }

          // Connection dropped after initial connect succeeded
          if (wasActive) {
            // Had an active request — show error immediately, don't wait for retries
            this.hasPendingMessage = false;
            this.errorCallbacks.forEach((cb) =>
              cb('Request timeout. The server is taking too long to respond. Please try again.')
            );
          }

          // Attempt reconnection if not a clean close (silently)
          if (event.code !== 1000) {
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
              this.scheduleReconnect();
            }
          }
        };
      } catch (error) {
        console.error('[WebSocket] Failed to create connection:', error);
        reject(error);
      }
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);

    console.log(
      `[WebSocket] Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`
    );

      this.reconnectTimeout = setTimeout(() => {
        console.log('[WebSocket] Attempting reconnect...');
        this.connect(
          this.connectionParams.userId,
          this.connectionParams.groupHash,
          this.connectionParams.sessionId,
          this.connectionParams.isPHQEnabled
        )
        .then(() => {
          console.log('[WebSocket] Reconnected successfully');
          this.reconnectCallbacks.forEach((cb) => cb());
        })
        .catch((error) => {
          console.error('[WebSocket] Reconnect failed:', error);
        });
    }, delay);
  }

  forceReconnect(): Promise<void> {
    console.log('[WebSocket] Force reconnect requested');
    this.reconnectAttempts = 0;
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    return this.connect(
      this.connectionParams.userId,
      this.connectionParams.groupHash,
      this.connectionParams.sessionId,
      this.connectionParams.isPHQEnabled
    );
  }

  private startPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }

    const PING_INTERVAL_MS = 15000;
    const PONG_TIMEOUT_MS = 10000;

    this.pingInterval = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        return;
      }

      // Send ping
      this.ws.send(JSON.stringify({ type: 'ping' }));

      // Check if last pong is too old
      const timeSinceLastPong = Date.now() - this.lastPongTime;
      if (timeSinceLastPong > PING_INTERVAL_MS + PONG_TIMEOUT_MS) {
        console.warn('[WebSocket] No pong — connection dead');
        if (this.ws) {
          this.ws.close();
        }
      }
    }, PING_INTERVAL_MS);
  }

  private startResponseTimeout(): void {
    this.clearResponseTimeout();
    this.responseTimeout = setTimeout(() => {
      if (this.hasPendingMessage) {
        console.warn('[WebSocket] Response timeout — no reply in', this.RESPONSE_TIMEOUT_MS, 'ms');
        this.hasPendingMessage = false;
        this.isStreaming = false;
        this.errorCallbacks.forEach((cb) =>
          cb('Request timeout. The server is taking too long to respond. Please try again.')
        );
      }
    }, this.RESPONSE_TIMEOUT_MS);
  }

  private clearResponseTimeout(): void {
    if (this.responseTimeout) {
      clearTimeout(this.responseTimeout);
      this.responseTimeout = null;
    }
  }

  private handleMessage(data: ServerMessage): void {
    // Any server message clears the response timeout
    this.clearResponseTimeout();

    switch (data.type) {
      case 'chunk':
        this.isStreaming = true;
        this.chunkCallbacks.forEach((cb) => cb(data.content));
        break;
      case 'end':
        this.isStreaming = false;
        this.hasPendingMessage = false;
        this.endCallbacks.forEach((cb) => cb(data.hooks || []));
        break;
      case 'error':
        this.isStreaming = false;
        this.hasPendingMessage = false;
        this.errorCallbacks.forEach((cb) => cb(data.error));
        break;
      case 'pong':
        // Just updates lastPongTime (done at top of handleMessage via onmessage)
        break;
      default:
        console.warn('[WebSocket] Unknown message type:', data);
    }
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.isStreaming = false;

    // Clear all callbacks
    this.chunkCallbacks = [];
    this.endCallbacks = [];
    this.errorCallbacks = [];
    this.openCallbacks = [];
    this.closeCallbacks = [];
    this.reconnectCallbacks = [];
  }

  sendMessage(message: WebSocketMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[WebSocket] Cannot send message - not connected');
      this.errorCallbacks.forEach((cb) =>
        cb('WebSocket is not connected')
      );
      return false;
    }

    const payload = JSON.stringify(message);
    this.ws.send(payload);
    this.hasPendingMessage = true;
    this.startResponseTimeout();
    return true;
  }

  onChunk(callback: ChunkCallback): () => void {
    this.chunkCallbacks.push(callback);
    return () => {
      this.chunkCallbacks = this.chunkCallbacks.filter((cb) => cb !== callback);
    };
  }

  onEnd(callback: EndCallback): () => void {
    this.endCallbacks.push(callback);
    return () => {
      this.endCallbacks = this.endCallbacks.filter((cb) => cb !== callback);
    };
  }

  onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.push(callback);
    return () => {
      this.errorCallbacks = this.errorCallbacks.filter((cb) => cb !== callback);
    };
  }

  onOpen(callback: OpenCallback): () => void {
    this.openCallbacks.push(callback);
    return () => {
      this.openCallbacks = this.openCallbacks.filter((cb) => cb !== callback);
    };
  }

  onClose(callback: CloseCallback): () => void {
    this.closeCallbacks.push(callback);
    return () => {
      this.closeCallbacks = this.closeCallbacks.filter((cb) => cb !== callback);
    };
  }

  onReconnect(callback: ReconnectCallback): () => void {
    this.reconnectCallbacks.push(callback);
    return () => {
      this.reconnectCallbacks = this.reconnectCallbacks.filter((cb) => cb !== callback);
    };
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  getReadyState(): number | null {
    return this.ws ? this.ws.readyState : null;
  }

  updateSessionId(sessionId: string): void {
    this.connectionParams.sessionId = sessionId;
  }
}

// Export singleton instance
export const chatWebSocket = new ChatWebSocketService();
